import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  LayoutGrid,
  Waves,
  Sprout,
  Bell,
  Inbox,
  LogOut,
  Loader2,
  Radio,
  Lock,
  CreditCard,
  Receipt,
  RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import IndiaMap from "@/components/IndiaMap";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const SEV_COLOR = { high: "#F43F5E", medium: "#FBBF24", low: "#10B981" };
const TYPE_COLOR = { ocean: "#00E5FF", agriculture: "#10B981" };
const LOCKED_VIEWS = ["ocean", "agriculture", "alerts"];

function StatCard({ label, value, suffix, trend, accent }) {
  return (
    <div className="border border-white/10 bg-arjuna-surface/50 p-6" data-testid="stat-card">
      <p className="text-xs text-slate-400">{label}</p>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-mono text-3xl font-semibold text-white sm:text-4xl">
          {typeof value === "number" ? value.toLocaleString("en-IN") : value}
          <span className="text-lg text-slate-400">{suffix}</span>
        </span>
        <span className="font-mono text-xs" style={{ color: accent }}>{trend}</span>
      </div>
    </div>
  );
}

function AlertRow({ a }) {
  return (
    <div className="flex items-start gap-4 border-b border-white/5 py-4" data-testid="alert-row">
      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: SEV_COLOR[a.severity] }} />
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wider" style={{ color: TYPE_COLOR[a.type] }}>
            {a.type}
          </span>
          <span className="font-mono text-[11px] text-slate-500">{a.time_ago}</span>
        </div>
        <p className="mt-1 text-sm text-slate-200">{a.message}</p>
        <p className="mt-1 font-mono text-[11px] text-slate-500">{a.region}</p>
      </div>
    </div>
  );
}

function UpgradeGate({ view }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-arjuna-surface/30 p-16 text-center" data-testid="upgrade-gate">
      <div className="flex h-16 w-16 items-center justify-center rounded-full border border-arjuna-teal/30 bg-arjuna-teal/10">
        <Lock className="h-7 w-7 text-arjuna-teal" />
      </div>
      <h3 className="mt-6 font-heading text-2xl capitalize text-white">Unlock {view} intelligence</h3>
      <p className="mt-3 max-w-md text-slate-400">
        The {view} layer and live anomaly feed are part of our paid plans. Upgrade to
        access full ocean &amp; agriculture intelligence for India.
      </p>
      <a href="/#pricing" data-testid="gate-upgrade-btn" className="btn-primary mt-8">
        View plans &amp; upgrade
      </a>
    </div>
  );
}

const BASE_NAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "ocean", label: "Ocean", icon: Waves },
  { id: "agriculture", label: "Agriculture", icon: Sprout },
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "billing", label: "Billing", icon: CreditCard },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("overview");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [requests, setRequests] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const isPaid = !!user?.is_paid;

  useEffect(() => {
    api.get("/dashboard/overview").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  useEffect(() => {
    if (view === "requests" && requests === null) {
      api.get("/demo-requests").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
    }
    if (view === "payments" && transactions === null) {
      api.get("/payments/transactions").then(({ data }) => setTransactions(data)).catch(() => setTransactions({ transactions: [], summary: {} }));
    }
  }, [view, requests, transactions]);

  const loadSubscription = useCallback(() => {
    api.get("/subscription").then(({ data }) => setSubscription(data)).catch(() => setSubscription(false));
  }, []);

  useEffect(() => {
    if (view === "billing") loadSubscription();
  }, [view, loadSubscription]);

  const nav = useMemo(() => {
    const items = [...BASE_NAV];
    if (isAdmin) {
      items.push({ id: "requests", label: "Demo Requests", icon: Inbox });
      items.push({ id: "payments", label: "Payments", icon: Receipt });
    }
    return items;
  }, [isAdmin]);

  const layer = view === "ocean" ? "ocean" : view === "agriculture" ? "agriculture" : "all";

  const filteredStats = useMemo(() => {
    if (!data) return [];
    if (layer === "all") return data.stats;
    return data.stats.filter((s) => s.type === layer || s.type === "mixed");
  }, [data, layer]);

  const filteredAlerts = useMemo(() => {
    if (!data) return [];
    if (layer === "all" || view === "alerts") return data.alerts;
    return data.alerts.filter((a) => a.type === layer);
  }, [data, layer, view]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const cancelSub = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Auto-renew turned off. You keep access until your period ends.");
      loadSubscription();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resumeSub = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Auto-renew re-enabled.");
      loadSubscription();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-arjuna-ink">
        <Loader2 className="h-7 w-7 animate-spin text-arjuna-teal" />
      </div>
    );
  }

  const gated = LOCKED_VIEWS.includes(view) && !isPaid;

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-white/10 bg-arjuna-ink p-6 lg:flex">
          <button onClick={() => navigate("/")} className="mb-10 flex items-center" data-testid="dash-logo">
            <Logo className="h-7 w-7" withWordmark />
          </button>
          <nav className="flex-1 space-y-1">
            {nav.map((n) => {
              const locked = LOCKED_VIEWS.includes(n.id) && !isPaid;
              return (
                <button
                  key={n.id}
                  data-testid={`nav-${n.id}`}
                  onClick={() => setView(n.id)}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm transition-colors duration-200 ${
                    view === n.id ? "bg-white/5 text-arjuna-teal" : "text-slate-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </span>
                  {locked && <Lock className="h-3.5 w-3.5 text-slate-600" />}
                </button>
              );
            })}
          </nav>
          <div className="border-t border-white/10 pt-4">
            <div className="mb-3 px-2">
              <p className="truncate text-sm text-white">{user?.name}</p>
              <p className="truncate font-mono text-[11px] text-slate-500">{user?.email}</p>
              <div className="mt-2 flex items-center gap-2">
                <span
                  data-testid="plan-badge"
                  className="rounded-full border border-arjuna-teal/40 bg-arjuna-teal/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-arjuna-teal"
                >
                  {isAdmin ? "Admin" : user?.plan || "Free"} plan
                </span>
                {!isPaid && !isAdmin && (
                  <a href="/#pricing" data-testid="upgrade-link" className="text-[11px] text-slate-400 hover:text-arjuna-teal">
                    Upgrade
                  </a>
                )}
              </div>
            </div>
            <button onClick={handleLogout} data-testid="dash-logout" className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-arjuna-teal">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1 p-6 lg:p-10">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-mono text-xs text-arjuna-teal">
                <Radio className="h-3.5 w-3.5 animate-pulse" /> LIVE · INDIA
              </p>
              <h1 className="mt-1 font-heading text-2xl font-medium capitalize tracking-tight sm:text-3xl">
                {view === "requests" ? "Demo Requests" : view === "payments" ? "Payments" : view === "billing" ? "Billing" : `${view} Console`}
              </h1>
            </div>
            <div className="flex flex-wrap gap-2 lg:hidden">
              {nav.map((n) => (
                <button key={n.id} onClick={() => setView(n.id)} className={`rounded-lg p-2 ${view === n.id ? "bg-white/10 text-arjuna-teal" : "text-slate-400"}`}>
                  <n.icon className="h-4 w-4" />
                </button>
              ))}
              <button onClick={handleLogout} className="rounded-lg p-2 text-slate-400"><LogOut className="h-4 w-4" /></button>
            </div>
          </div>

          {view === "requests" ? (
            <RequestsTable requests={requests} />
          ) : view === "payments" ? (
            <PaymentsView data={transactions} />
          ) : view === "billing" ? (
            <BillingView sub={subscription} busy={busy} onCancel={cancelSub} onResume={resumeSub} isAdmin={isAdmin} />
          ) : gated ? (
            <UpgradeGate view={view} />
          ) : (
            <motion.div key={view} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 sm:grid-cols-2 lg:grid-cols-4">
                {filteredStats.map((s) => (
                  <StatCard key={s.id} label={s.label} value={s.value} suffix={s.suffix} trend={s.trend}
                    accent={s.type === "agriculture" ? "#10B981" : "#00E5FF"} />
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
                <div className="rounded-2xl border border-white/10 bg-arjuna-surface/30 p-6 lg:col-span-7">
                  <div className="mb-4 flex items-center justify-between">
                    <p className="overline">Monitoring Grid</p>
                    <p className="font-mono text-xs text-slate-500">
                      {data.map_points.filter((p) => layer === "all" || p.type === layer).length} nodes
                    </p>
                  </div>
                  <div className="mx-auto max-w-sm">
                    <IndiaMap points={data.map_points} activeLayer={layer} onSelect={setSelected} />
                  </div>
                  {selected && (
                    <div className="mt-4 flex items-center justify-between rounded-xl bg-white/5 px-5 py-4" data-testid="dash-selected">
                      <div>
                        <p className="font-heading text-white">{selected.name}</p>
                        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{selected.region} · {selected.metric}</p>
                      </div>
                      <span className="font-mono text-lg font-semibold" style={{ color: TYPE_COLOR[selected.type] }}>{selected.value}</span>
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-arjuna-surface/30 p-6 lg:col-span-5">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="overline">Anomaly Feed</p>
                    <span className="font-mono text-xs text-slate-500">{filteredAlerts.length}</span>
                  </div>
                  {isPaid ? (
                    <div className="max-h-[420px] overflow-y-auto pr-1" data-testid="alerts-feed">
                      {filteredAlerts.map((a) => <AlertRow key={a.id} a={a} />)}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="alerts-locked">
                      <Lock className="h-6 w-6 text-slate-600" />
                      <p className="mt-3 text-sm text-slate-400">Live anomaly alerts are a paid feature.</p>
                      <a href="/#pricing" className="mt-4 text-sm text-arjuna-teal hover:underline">Upgrade to unlock</a>
                    </div>
                  )}
                </div>
              </div>

              {view !== "alerts" && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-arjuna-surface/30 p-6">
                  <p className="overline mb-6">Signal Index. 8 month trend</p>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.trend} margin={{ left: -20, right: 8 }}>
                        <defs>
                          <linearGradient id="gOcean" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gAgri" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{ background: "#0F172A", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 12, color: "#fff" }}
                          cursor={{ stroke: "rgba(255,255,255,0.1)" }}
                        />
                        {(layer === "all" || layer === "ocean") && (
                          <Area type="monotone" dataKey="ocean" stroke="#00E5FF" strokeWidth={2} fill="url(#gOcean)" name="Ocean" />
                        )}
                        {(layer === "all" || layer === "agriculture") && (
                          <Area type="monotone" dataKey="agri" stroke="#10B981" strokeWidth={2} fill="url(#gAgri)" name="Agriculture" />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </main>
      </div>
    </div>
  );
}

function BillingView({ sub, busy, onCancel, onResume, isAdmin }) {
  const navigate = useNavigate();
  if (sub === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-arjuna-teal" /></div>;
  }
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : " ");

  return (
    <div className="max-w-2xl" data-testid="billing-view">
      <div className="rounded-2xl border border-white/10 bg-arjuna-surface/30 p-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline">Current plan</p>
            <h2 className="mt-3 font-heading text-3xl text-white" data-testid="billing-plan">
              {isAdmin ? "Admin (all access)" : sub.plan}
            </h2>
            {sub.is_paid && (
              <p className="mt-2 font-mono text-xs text-slate-400">
                {sub.billing_cycle === "annual" ? "Annual" : "Monthly"} · renews {fmt(sub.plan_expires)} ·{" "}
                <span className={sub.auto_renew ? "text-arjuna-agri" : "text-amber-400"}>
                  {sub.auto_renew ? "auto-renew on" : "cancels at period end"}
                </span>
              </p>
            )}
          </div>
          <span className="rounded-full border border-arjuna-teal/40 bg-arjuna-teal/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-arjuna-teal">
            {isAdmin ? "Admin" : sub.plan}
          </span>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          {sub.is_paid && !isAdmin ? (
            <>
              <button data-testid="billing-change" onClick={() => navigate("/#pricing")} className="btn-ghost">
                Change plan
              </button>
              {sub.auto_renew ? (
                <button data-testid="billing-cancel" onClick={onCancel} disabled={busy} className="btn-ghost">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Cancel subscription
                </button>
              ) : (
                <button data-testid="billing-resume" onClick={onResume} disabled={busy} className="btn-primary">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Resume auto-renew
                </button>
              )}
            </>
          ) : !isAdmin ? (
            <a href="/#pricing" data-testid="billing-upgrade" className="btn-primary">Upgrade your plan</a>
          ) : (
            <p className="text-sm text-slate-400">Admin accounts have full access to all features.</p>
          )}
        </div>
      </div>

      <p className="mt-4 font-mono text-xs text-slate-500">
        App-managed billing (test mode). Cancelling turns off renewal; access continues until the period ends.
      </p>
    </div>
  );
}

function PaymentsView({ data }) {
  if (data === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-arjuna-teal" /></div>;
  }
  const { transactions = [], summary = {} } = data;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : " ");
  const statusColor = (s) => (s === "paid" ? "#10B981" : s === "initiated" ? "#FBBF24" : "#94A3B8");

  return (
    <div data-testid="payments-view">
      <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 sm:grid-cols-3">
        {[
          ["Transactions", summary.total ?? 0, ""],
          ["Paid", summary.paid ?? 0, ""],
          ["Revenue", `$${(summary.revenue ?? 0).toLocaleString("en-US")}`, ""],
        ].map(([label, val]) => (
          <div key={label} className="bg-arjuna-surface/50 p-6">
            <p className="text-xs text-slate-400">{label}</p>
            <p className="mt-2 font-mono text-2xl font-semibold text-white">{val}</p>
          </div>
        ))}
      </div>

      {transactions.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-arjuna-surface/30 p-16 text-center" data-testid="payments-empty">
          <Receipt className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-slate-400">No transactions yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="payments-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-arjuna-surface/60 font-mono text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Plan</th>
                <th className="hidden px-6 py-4 md:table-cell">Cycle</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.session_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-6 py-4 font-mono text-xs text-slate-500">{fmt(t.created_at)}</td>
                  <td className="px-6 py-4 text-slate-300">{t.user_email || "Guest"}</td>
                  <td className="px-6 py-4 text-white">{t.plan_name}</td>
                  <td className="hidden px-6 py-4 text-slate-300 md:table-cell">{t.billing_cycle || "monthly"}</td>
                  <td className="px-6 py-4 font-mono text-white">${(t.amount || 0).toLocaleString("en-US")}</td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs uppercase" style={{ color: statusColor(t.payment_status) }}>
                      {t.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RequestsTable({ requests }) {
  if (requests === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-arjuna-teal" /></div>;
  }
  if (!requests.length) {
    return (
      <div className="rounded-2xl border border-white/10 bg-arjuna-surface/30 p-16 text-center" data-testid="requests-empty">
        <Inbox className="mx-auto h-10 w-10 text-slate-600" />
        <p className="mt-4 text-slate-400">No demo requests yet.</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="requests-table">
      <table className="w-full text-left text-sm">
        <thead className="bg-arjuna-surface/60 font-mono text-[11px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className="px-6 py-4">Name</th>
            <th className="px-6 py-4">Email</th>
            <th className="hidden px-6 py-4 md:table-cell">Organization</th>
            <th className="hidden px-6 py-4 md:table-cell">Sector</th>
            <th className="px-6 py-4">Received</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} className="border-t border-white/5 hover:bg-white/5">
              <td className="px-6 py-4 text-white">{r.name}</td>
              <td className="px-6 py-4 text-arjuna-teal">{r.email}</td>
              <td className="hidden px-6 py-4 text-slate-300 md:table-cell">{r.organization || " "}</td>
              <td className="hidden px-6 py-4 text-slate-300 md:table-cell">{r.sector || " "}</td>
              <td className="px-6 py-4 font-mono text-xs text-slate-500">
                {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
